import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloError } from '@apollo/client/errors';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import Form from '@lib/components/ReactHookForm/FormContainer';
import { FieldItem, Values } from '@lib/interfaces/form';
import Modal from '@lib/components/Modal/Modal';
import CategoryForm from 'components/FormCategory/CategoryForm';
import { FormTemplateMode } from 'routes/FormTemplate/enums';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import FormTemplateFieldNames from '@lib/enums/fieldNames/formTemplateFieldNames';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import { formTemplateBuilderScrollboxId } from '@lib/enums/selectors';
import FormTemplateBuilderActions from '../FormTemplateBuilderActions/FormTemplateBuilderActions';
import FormTemplateBuilderDefaultQuestions from '../FormTemplateBuilderDefaultQuestions/FormTemplateBuilderDefaultQuestions';
import getPrefilledQuestions from '../utils/getPrefilledQuestions';
import styles from './FormTemplateBuilderForm.module.scss';

interface Props {
  closeCategoryFormModal: () => void;
  formCategoryCreateMutationError?: ApolloError;
  formCategoryCreateMutationLoading: boolean;
  formFields: FieldItem[];
  formRef: FormRefType;
  formTemplateCreateMutationLoading: boolean;
  formTemplateData: Values;
  formTemplateVersionData: Values;
  formTemplateError?: ApolloError;
  formTemplateGraphQLErrors?: ApolloError;
  formTemplateLoading: boolean;
  formTemplateUpdateMutationLoading: boolean;
  isCategoryFormModalOpened: boolean;
  onCancelForm: () => void;
  onFormCategoryCreate: (values: Values) => void;
  onSubmitForm: (values: Values) => void;
  onViewHandler: () => void;
  viewMode: FormTemplateMode;
  formTemplateVersionCreateMutationLoading: boolean;
  formTemplateVersionUpdateMutationLoading: boolean;
}

function FormTemplateBuilderForm(props: Props) {
  const {
    closeCategoryFormModal,
    formCategoryCreateMutationError,
    formCategoryCreateMutationLoading,
    formFields,
    formRef,
    formTemplateCreateMutationLoading,
    formTemplateData,
    formTemplateVersionData,
    formTemplateError,
    formTemplateGraphQLErrors,
    formTemplateLoading,
    formTemplateUpdateMutationLoading,
    isCategoryFormModalOpened,
    onCancelForm,
    onFormCategoryCreate,
    onSubmitForm,
    onViewHandler,
    viewMode,
    formTemplateVersionCreateMutationLoading,
    formTemplateVersionUpdateMutationLoading,
  } = props;

  const { t } = useTranslation();

  const prefilledQuestions = getPrefilledQuestions({ t });

  const isFormLoading = () =>
    formTemplateCreateMutationLoading ||
    formTemplateUpdateMutationLoading ||
    formTemplateVersionCreateMutationLoading ||
    formTemplateVersionUpdateMutationLoading ||
    formTemplateLoading;

  const shouldShowPreviewButton = () =>
    viewMode === FormTemplateMode.Edit ||
    viewMode === FormTemplateMode.EditVersion;

  const shouldShowDefaultQuestions = () =>
    viewMode === FormTemplateMode.Add ||
    viewMode === FormTemplateMode.Duplicate ||
    viewMode === FormTemplateMode.EditVersion;

  const getPageTitle = () => {
    const formBuilderTitle = t('form-builder');
    if (viewMode === FormTemplateMode.Duplicate) {
      return `${formBuilderTitle} (${t('create-duplicate')})`;
    }
    return formBuilderTitle;
  };

  const renderAfterForm = shouldShowDefaultQuestions()
    ? FormTemplateBuilderDefaultQuestions
    : undefined;

  const defaultValues = {
    ...formTemplateData,
    questions: formTemplateVersionData?.questions,
  };

  const actionsComponentProps = {
    onCancelForm,
    viewMode,
  };

  const sourceFields = {
    questions: prefilledQuestions,
  };

  const graphQLErrorsReplacePathMap = {
    'version.questions': FormTemplateFieldNames.Questions,
    questions: FormTemplateFieldNames.Questions,
  };

  if (formTemplateError) {
    return (
      <ErrorPage
        status={getStatusFromError(formTemplateError)}
        description={formTemplateError?.message}
      />
    );
  }

  return (
    <div className={styles.builderGrid}>
      <div
        id={formTemplateBuilderScrollboxId}
        className={styles.builderScrollbox}
      >
        <PageHeader title={getPageTitle()}>
          {shouldShowPreviewButton() ? (
            <Button
              buttonText={t('preview')}
              buttonType={ButtonTypes.primaryOutlined}
              leftIcon="visibility"
              onClick={onViewHandler}
            />
          ) : (
            <div />
          )}
        </PageHeader>
        <PageBody>
          <div>
            <Form
              defaultValues={defaultValues}
              actionsComponent={FormTemplateBuilderActions}
              actionsComponentProps={actionsComponentProps}
              fields={formFields}
              formRef={formRef}
              onSubmit={onSubmitForm}
              renderAfterForm={renderAfterForm}
              sourceFields={sourceFields}
              loading={isFormLoading()}
              graphQLErrors={formTemplateGraphQLErrors?.graphQLErrors}
              graphQLErrorsReplacePathMap={graphQLErrorsReplacePathMap}
            />
          </div>
        </PageBody>
      </div>
      <Modal
        isOpen={isCategoryFormModalOpened}
        title={t('create-new-form-category')}
        onClose={closeCategoryFormModal}
      >
        <CategoryForm
          defaultValues={{}}
          graphQLErrors={formCategoryCreateMutationError?.graphQLErrors}
          loading={formCategoryCreateMutationLoading}
          onCancel={closeCategoryFormModal}
          onSubmit={onFormCategoryCreate}
        />
      </Modal>
    </div>
  );
}

export default memo(FormTemplateBuilderForm);
